import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Button as ReactstrapButton,
  Card,
  CardBody,
  CardHeader,
} from "reactstrap";
import "./style.css";
import AxiosInstance from "../../AxiosInstance";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Grid, Button } from "@mui/material";
import { LoaderComponent } from "../../../components/Icon/Index";
import { Typography, Tooltip } from "@mui/material";
import { useUserType } from "../../../contexts/UserTypeContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlueButton from "../../../components/Button/BlueButton";
import { clear } from "../../../utils/storage";

function Index() {
  const baseUrl = import.meta.env.VITE_BASE_API;
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const [plans, setPlans] = useState([]);
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await AxiosInstance.get(`/v1/plan/plan`);
        if (response?.data?.statusCode === 200 && response?.data?.data) {
          const mostPopularItem = response?.data?.data?.find(
            (item) => item?.IsMostpopular
          );
          const otherItems = response?.data?.data?.filter(
            (item) => !item?.IsMostpopular
          );
          const middleIndex = Math?.floor(otherItems?.length / 2);
          const sortedItems = [
            ...otherItems?.slice(0, middleIndex),
            mostPopularItem,
            ...otherItems?.slice(middleIndex),
          ];
          setPlans(sortedItems?.filter((plan) => plan));
        } else {
          console.error("Failed to fetch data", response);
        }
      } catch (error) {
        console.error("There was an error fetching the data!", error);
      } finally {
        setLoader(false);
      }
    };

    fetchPlans();
  }, [baseUrl]);

  useLayoutEffect(() => {
    if (loader) return;
    const container = document.querySelector(".plan-modern-pricing-container");
    if (!container) return;

    const recompute = () => {
      const headers = container.querySelectorAll(".plan-modern-card-header");
      let maxHeight = 0;
      headers.forEach((el) => {
        const h = el.getBoundingClientRect().height;
        if (h > maxHeight) maxHeight = h;
      });
      if (maxHeight > 0) {
        container.style.setProperty(
          "--plan-header-height",
          `${Math.ceil(maxHeight)}px`
        );
      }
    };

    requestAnimationFrame(recompute);

    const ro = new ResizeObserver(recompute);
    container
      .querySelectorAll(".plan-modern-card-header, .plan-description")
      .forEach((el) => ro.observe(el));
    window.addEventListener("resize", recompute);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [loader, plans]);

  const mostPopularIndex = plans?.findIndex((plan) => plan?.IsMostpopular);
  let reorderedPlans = [...plans];
  if (mostPopularIndex !== -1) {
    const [mostPopularPlan] = reorderedPlans?.splice(mostPopularIndex, 1);
    reorderedPlans?.splice(1, 0, mostPopularPlan);
  }

  function getPlanFrequencyDisplay(monthFrequency) {
    if (monthFrequency === "12 (Annually)") {
      return "year";
    } else if (monthFrequency === "1") {
      return "month";
    } else {
      return `${monthFrequency?.split(" ")[0]} months`;
    }
  }

  const handleLogout = () => {
    try {
      clear();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      navigate("/auth/login", { replace: true });
    }
  };

  return (
    <div className="plan-modern-pricing-container">
      <div
        className="plan-header-section"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "0px",
        }}
      >
        <BlueButton
          className="text-capitalize"
          isDonationMode={isDonationMode}
          label={
            <ArrowBackIcon/>
          }
          onClick={() => navigate(-1)}
        />
        {!CompanyUrl && (
          <ReactstrapButton
            className={`plan-logout-button ${
              isDonationMode ? "bg-green-color" : "bg-orange-color"
            } text-white-color`}
            onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt me-2"></i>
            Logout
          </ReactstrapButton>
        )}
      </div>

      <div className="plan-pricing-content">
        <div className="plan-pricing-header">
          <Typography
            className={`plan-main-title ${
              isDonationMode ? "text-donor-color" : "text-blue-color"
            }`}
          >
            Select Your Plan
          </Typography>
          <Typography className="plan-main-subtitle">
            Compare the plans to determine which one is most suitable for your
            business.
          </Typography>

          <div
            className={`plan-promo-banner ${
              isDonationMode ? "bg-white-donor-color" : "bg-orange-light-color"
            }`}
          >
            <Typography
              className={`plan-promo-text ${
                isDonationMode ? "text-green-color" : "text-orange-color"
              }`}
            >
              Unlock significant savings with an annual subscription
            </Typography>
          </div>
        </div>

        <div className="plan-plans-grid">
          {loader ? (
            <div className="plan-loader-container">
              <LoaderComponent variant="skeleton"
                isDonationMode={isDonationMode}
                loader={loader}
                height="50"
                width="50"
              />
            </div>
          ) : (
            <div className="plan-plans-wrapper">
              {reorderedPlans?.map((plan) =>
                plan ? (
                  <div key={plan?._id} className="plan-card-wrapper">
                    <Card
                      className={`plan-modern-card ${
                        plan?.IsMostpopular
                          ? `plan-popular-card border-${
                              isDonationMode ? "green" : "orange"
                            }-color`
                          : ""
                      }`}
                    >
                      {plan?.IsMostpopular && (
                        <div
                          className={`plan-popular-badge ${
                            isDonationMode
                              ? "bg-green-color"
                              : "bg-orange-color"
                          }`}
                        >
                          <i className="fas fa-star"></i>
                          Most Popular
                        </div>
                      )}

                      <CardHeader className="plan-modern-card-header">
                        <Typography
                          className={`plan-name ${
                            isDonationMode
                              ? "text-donor-color"
                              : "text-blue-color"
                          }`}
                        >
                          {plan?.PlanName || "Plan Name"}
                        </Typography>
                        <ClampedReadMore
                          text={
                            plan?.PlanDetail || "Plan details not available"
                          }
                          lines={8}
                        />
                      </CardHeader>

                      <CardBody className="plan-modern-card-body">
                        <div className="plan-pricing-section">
                          <div className="plan-price-display">
                            <span
                              className={`plan-price-amount ${
                                isDonationMode
                                  ? "text-green-color"
                                  : "text-orange-color"
                              }`}
                            >
                              ${Number(plan?.PlanPrice).toFixed(0)}
                            </span>
                            <span className="plan-price-period">
                              /{getPlanFrequencyDisplay(plan?.MonthFrequency)}
                            </span>
                          </div>
                          <Typography className="plan-monthly-price">
                            $
                            {(
                              Number(plan?.PlanPrice) /
                              Number(plan?.MonthFrequency?.split(" ")[0])
                            ).toFixed(2)}
                            /month
                          </Typography>
                        </div>

                        <ReactstrapButton
                          className={`plan-select-button ${
                            isDonationMode
                              ? plan?.IsMostpopular
                                ? "bg-green-color"
                                : "bg-donor-color"
                              : plan?.IsMostpopular
                              ? "bg-orange-color"
                              : "bg-blue-color"
                          }`}
                          onClick={() => {
                            if (CompanyUrl) {
                              navigate(`/${CompanyUrl}/plan-purchase`, {
                                state: {
                                  navigats: [
                                    ...location?.state?.navigats,
                                    "/plan-purchase",
                                  ],
                                  plan,
                                  card: location?.state?.card || "",
                                },
                              });
                            } else {
                              navigate("/plan-purchase", {
                                state: {
                                  plan,
                                },
                              });
                            }
                          }}
                        >
                          {location?.state?.plan
                            ? "Upgrade Plan"
                            : "Select Plan"}
                        </ReactstrapButton>
                      </CardBody>
                    </Card>
                  </div>
                ) : null
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Index;

function ClampedReadMore({ text = "", lines = 8 }) {
  const wrapperRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleText, setVisibleText] = useState("");
  const [needsClamp, setNeedsClamp] = useState(false);

  const recompute = () => {
    const wrapper = wrapperRef.current;
    const measurer = measureRef.current;
    if (!wrapper || !measurer) return;

    const fullText = String(text || "");
    if (!fullText) {
      setVisibleText("");
      setNeedsClamp(false);
      return;
    }

    const { clientWidth } = wrapper;
    if (clientWidth <= 0) {
      setVisibleText(fullText);
      setNeedsClamp(false);
      return;
    }

    measurer.style.width = `${clientWidth}px`;

    const computed = window.getComputedStyle(measurer);
    const lineHeight = parseFloat(computed.lineHeight || "0");
    if (!lineHeight) {
      setVisibleText(fullText);
      setNeedsClamp(false);
      return;
    }

    const maxHeight = lineHeight * lines;

    measurer.textContent = fullText;
    if (measurer.scrollHeight <= maxHeight) {
      setVisibleText(fullText);
      setNeedsClamp(false);
      return;
    }

    const suffix = " … ";
    let low = 0;
    let high = fullText.length;
    let best = 0;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = `${fullText.slice(0, mid)}${suffix}Read more`;
      measurer.textContent = candidate;
      if (measurer.scrollHeight <= maxHeight) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    const finalText = fullText.slice(0, Math.max(0, best));
    setVisibleText(finalText);
    setNeedsClamp(true);
  };

  useLayoutEffect(() => {
    recompute();
    const ro = new ResizeObserver(() => recompute());
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    window.addEventListener("orientationchange", recompute);
    window.addEventListener("resize", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", recompute);
      window.removeEventListener("resize", recompute);
    };
  }, [text, lines]);

  return (
    <div ref={wrapperRef} className="plan-description-wrap">
      <Typography className="plan-description">
        {needsClamp ? (
          <>
            {visibleText} …{" "}
            <Tooltip
              arrow
              placement="top"
              title={<span className="plan-tooltip-content">{text}</span>}
            >
              <span className="plan-inline-readmore" role="button" tabIndex={0}>
                Read more
              </span>
            </Tooltip>
          </>
        ) : (
          text || "Plan details not available"
        )}
      </Typography>

      <div
        ref={measureRef}
        className="plan-description plan-description-measurer"
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          left: 0,
          top: 0,
        }}
      />
    </div>
  );
}
